import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [trialNotification, setTrialNotification] = useState(null);
  const [loading, setLoading] = useState(true);

  // Charger l'abonnement
  const fetchSubscription = useCallback(async () => {
    if (!token) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Erreur chargement abonnement:', error);
      // Plan gratuit par défaut
      setSubscription({
        plan_id: 'free',
        plan_name: 'Gratuit',
        status: 'active',
        can_export: true,
        can_add_transactions: true,
        show_upgrade_banner: false
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger la notification d'essai
  const fetchTrialNotification = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/api/subscription/trial-notification`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialNotification(response.data);
    } catch (error) {
      console.error('Erreur notification:', error);
    }
  }, [token]);

  // Charger au démarrage
  useEffect(() => {
    fetchSubscription();
    fetchTrialNotification();
  }, [fetchSubscription, fetchTrialNotification]);

  // Utiliser un export (renommé pour éviter la confusion avec les hooks React)
  const trackExport = async () => {
    if (!token) return false;

    try {
      await axios.post(`${API_URL}/api/subscription/use-export`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Recharger l'abonnement pour mettre à jour le compteur
      await fetchSubscription();
      return true;
    } catch (error) {
      console.error('Erreur comptabilisation export:', error);
      return false;
    }
  };

  // Vérifier si l'utilisateur peut exporter
  const canExport = () => {
    if (!subscription) return false;
    return subscription.can_export;
  };

  // Vérifier si l'utilisateur est en période d'essai
  const isInTrial = () => {
    if (!subscription?.trial_info) return false;
    return subscription.trial_info.is_in_trial;
  };

  // Vérifier si l'essai est expiré
  const isTrialExpired = () => {
    if (!subscription?.trial_info) return false;
    return subscription.trial_info.trial_expired;
  };

  // Obtenir les jours restants
  const getTrialDaysRemaining = () => {
    if (!subscription?.trial_info) return 0;
    return subscription.trial_info.trial_days_remaining;
  };

  // Vérifier si on doit montrer la bannière d'upgrade
  const shouldShowUpgradeBanner = () => {
    if (!subscription) return false;
    return subscription.show_upgrade_banner;
  };

  // Vérifier si l'utilisateur a un plan payant
  const hasPaidPlan = () => {
    if (!subscription) return false;
    return subscription.plan_id !== 'free';
  };

  // Vérifier si le PDF doit avoir un filigrane
  const hasWatermark = () => {
    if (!subscription) return true; // Par défaut, filigrane
    return subscription.has_watermark === true;
  };

  // Créer une session portail client Stripe
  const createCustomerPortalSession = async () => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/subscription/create-customer-portal-session`,
        { return_url: window.location.origin + '/app/parametres' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.url;
    } catch (error) {
      console.error('Erreur création session portail:', error);
      return null;
    }
  };

  const value = {
    subscription,
    trialNotification,
    loading,
    fetchSubscription,
    fetchTrialNotification,
    trackExport,
    canExport,
    isInTrial,
    isTrialExpired,
    getTrialDaysRemaining,
    shouldShowUpgradeBanner,
    hasPaidPlan,
    hasWatermark,
    createCustomerPortalSession
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
